/**
 * Eine Frage im Review — EINE Zeile.
 *
 * Vorher war das eine Karte: Antworttyp-Chip, Anker-Chip, alle Quellen, zwei
 * graue Textblöcke und vier Knöpfe. Bei 70 Fragen ergab das 20.084 px Scrollweg
 * für einen Bogen, der als PDF acht Seiten hat — der Prüfer las das System
 * länger als das Dokument (Tester-Rückmeldung 20.08.: „gefühlt alles sehr viel
 * länger als der Fragebogen selbst"). Die Zeile dreht das um:
 *
 * * **Was sicher ist, steht als ERGEBNIS da** (WH 2026-08-26, L'Oréal-Bogen).
 *   Ein Haken je Zeile — auch für „Supplier name → bitop AG" mit Konfidenz
 *   0,95 — machte das Werkzeug langsamer als das Formular von Hand. Ein
 *   sicherer Vorschlag wird deshalb als Klartext mit leisem „Ändern" gezeigt;
 *   die Freigabe verschwindet nicht, sie wandert auf EINE Attestierung am Ende
 *   („Geprüft — Dokument ausfüllen", `Review.tsx`). Was ein Mensch wirklich
 *   entscheiden muss — mittlere/unsichere Vorschläge, Lücken, Entscheidungen —
 *   behält die volle, unübersehbare Bedienung.
 * * **Ein Klick statt drei**, wo noch geklickt wird: Yes/No/N/A stehen IN der
 *   Zeile. Ein Klick auf den vorgeschlagenen Wert ist „übernehmen", ein Klick
 *   auf einen anderen ist die Korrektur — je genau ein PATCH, sofort
 *   gespeichert.
 * * **Frage links, Antwort rechts.** Ab 600 px trennt ein Raster die beiden;
 *   die Antwortspalte darf wachsen und mehrzeilig werden. Vorher rangen
 *   Fragetext, Chip, Segmente, Ampel, Haken und ⓘ um EINE flex-wrap-Zeile und
 *   zerrissen sie auf schmaleren Fenstern in zwei bis drei Bruchstücke.
 * * **Kein Technik-Vokabular.** „Ja/Nein" und „pdf_geometry" sagten dem QM
 *   nichts. Der Anker steht nur noch da, wo er FEHLT — dann ist er eine
 *   Warnung („nicht automatisch einfügbar") und keine Beschriftung.
 * * **Auf der Zeile bleibt, was die Entscheidung trägt**: Konfidenz-Ampel,
 *   der Kern-Satz einer maschinellen Entscheidung/Lücke und Warnungen, die
 *   Handeln verlangen. Belege, Vorbehalte und Nebenaktionen liegen hinter ⓘ.
 * * **Die Langfassung ist beschriftet.** `answer` geht ins Formularfeld,
 *   `answer_long` NUR in den Antwortbogen (`writer.py`). Untereinander ohne
 *   Beschriftung las sich das wie doppelter Text.
 */
import { useState } from "react";
import {
  AlertTriangle,
  Check,
  Info,
  Paperclip,
  Search,
  SkipForward,
} from "lucide-react";
import clsx from "clsx";

import { useT } from "../i18n";
import { Button, Input, Select, StatusBadge, Textarea } from "../ui";
import type {
  FinaleAntwort,
  Frage,
  KontaktFeld,
  KontaktVerzeichnisEintrag,
  KontaktVorlage,
  ReviewState,
  Vorschlag,
} from "./api";
import { KONFIDENZ_PUNKT, anzeigeNummer, datum, konfidenzInfo } from "./labels";

/**
 * Die drei Werte, die die Synthese für `yes_no` normalisiert (synthesis.py).
 *
 * **Sie werden NICHT übersetzt** — und das ist kein vergessener Katalog-Eintrag
 * (T9 D-11): was hier steht, wird vom Writer wörtlich in das Kundendokument
 * geschrieben, und das ist ein englischer Fragebogen. Ein „Ja" in einer
 * Intertek-Tabelle wäre ein falscher Dokumenteninhalt, kein übersetztes Label.
 * Erklärt wird das über den Tooltip `jaNeinHinweis` — der ist Oberfläche und
 * steht deshalb sehr wohl im Katalog.
 */
const JA_NEIN = ["Yes", "No", "N/A"];

/**
 * Der Grund einer maschinell entschiedenen Frage — aus dem Katalog, nicht vom
 * Server.
 *
 * Der Server liefert `grund_code` (maschinenlesbar) UND `grund` (deutscher
 * Satz). Gezeigt wird der Katalog-Text, sonst stünde in der englischen Maske
 * ein deutscher Satz; der Server-Satz bleibt der Rückfall für Codes, die diese
 * Oberfläche noch nicht kennt — sichtbarer Text schlägt leere Fläche.
 * Dieselbe Trennung wie beim Norm-Guard (`norm_konflikt`).
 */
export function grundText(
  s: Record<string, any>,
  t: ReturnType<typeof useT>["questionnaires"]
): string {
  const a = s.anlage ?? {};
  const titel = a.bester?.title ?? "";
  switch (s.grund_code) {
    case "anlage_fehlt":
      return t.grundAnlageFehlt;
    case "anlage_unsicher":
      return t.grundAnlageUnsicher(titel, (a.bester?.score ?? 0).toFixed(2));
    case "anlage_abgelaufen":
      return t.grundAnlageAbgelaufen(titel, datum(a.bester?.valid_until ?? ""));
    case "anlage_vertraulich":
      return t.grundAnlageVertraulich(titel);
    case "gating_elternantwort":
      return t.grundGatingElternantwort(
        anzeigeNummer(s.gating?.eltern_position),
        s.gating?.eltern_antwort ?? ""
      );
    case "gating_sektion":
      return t.grundGatingSektion(
        s.gating?.praefix ?? "",
        anzeigeNummer(s.gating?.eltern_position),
        s.gating?.eltern_antwort ?? ""
      );
    case "signoff_signatur":
      return t.grundSignoffSignatur;
    case "signoff_titel_fehlt":
      return t.grundSignoffTitelFehlt(s.signoff?.name ?? "");
    case "signoff_kein_nutzer":
      return t.grundSignoffKeinNutzer;
    default:
      return String(s.grund ?? "");
  }
}

export function optionLabels(frage: Frage): string[] {
  return frage.options
    .map((o) => (typeof o === "string" ? o : String(o?.label ?? "")))
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Eingabefeld passend zum Antworttyp — für den Inline-Editor und den
 * Entscheidungs-Block.
 *
 * `date` bekommt bewusst KEIN `input[type=date]`: das native Feld zeigt sein
 * Format nach der Prozess-Sprache des Browsers (MM/DD/YYYY im englischen
 * Chrome), und die Antwort geht als Text ins Kundenformular. Ein Textfeld mit
 * deutschem Platzhalter ist hier das ehrlichere Werkzeug.
 */
export function AntwortFeld({
  frage,
  wert,
  onChange,
  autoFocus,
}: {
  frage: Frage;
  wert: string;
  onChange: (w: string) => void;
  autoFocus?: boolean;
}) {
  const t = useT().questionnaires;
  const optionen = optionLabels(frage);

  if (frage.answer_type === "yes_no" || frage.answer_type === "attachment") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {JA_NEIN.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            aria-pressed={wert === v}
            title={t.jaNeinHinweis}
            className={clsx(
              "px-3 py-1.5 rounded-md text-sm font-medium border transition-colors",
              wert === v
                ? "bg-accent-500 border-accent-500 text-white"
                : "bg-surface border-border text-text-2 hover:border-accent-300"
            )}
          >
            {v}
          </button>
        ))}
        <Input
          value={JA_NEIN.includes(wert) ? "" : wert}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t.eigenerText}
          className="flex-1 min-w-[10rem]"
        />
      </div>
    );
  }

  if (frage.answer_type === "choice" && optionen.length > 0) {
    return (
      <Select value={wert} onChange={(e) => onChange(e.target.value)} autoFocus={autoFocus}>
        <option value="">{t.bitteWaehlen}</option>
        {optionen.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
        {wert && !optionen.includes(wert) && <option value={wert}>{wert}</option>}
      </Select>
    );
  }

  if (frage.answer_type === "text_long" || frage.answer_type === "table") {
    return (
      <Textarea
        rows={4}
        value={wert}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
      />
    );
  }

  return (
    <Input
      value={wert}
      onChange={(e) => onChange(e.target.value)}
      autoFocus={autoFocus}
      placeholder={frage.answer_type === "date" ? t.datumPlatzhalter : undefined}
      inputMode={frage.answer_type === "number" ? "decimal" : undefined}
    />
  );
}

/**
 * Yes | No | N/A als Segment-Leiste — der Ein-Klick-Pfad.
 *
 * Zwei Zustände, die man auseinanderhalten muss: der VORGESCHLAGENE Wert ist
 * nur getönt (ein Angebot), der GESPEICHERTE ist gefüllt (eine Entscheidung).
 * Sähen beide gleich aus, könnte der Prüfer nicht sehen, was er schon
 * freigegeben hat — und genau das ist die Frage, die er beim Scrollen stellt.
 */
function JaNeinLeiste({
  wert,
  gespeichert,
  busy,
  onWaehlen,
}: {
  /** Der geltende Wert: die freigegebene Antwort, sonst der Vorschlag. */
  wert: string;
  /** Ist dieser Wert schon gespeichert (accepted/edited) oder nur vorgeschlagen? */
  gespeichert: boolean;
  busy: boolean;
  onWaehlen: (wert: string) => void;
}) {
  const t = useT().questionnaires;
  return (
    <div className="inline-flex shrink-0 divide-x divide-border overflow-hidden rounded-md border border-border">
      {JA_NEIN.map((v) => {
        const aktiv = wert === v;
        return (
          <button
            key={v}
            type="button"
            disabled={busy}
            data-antwort-wert={v}
            aria-pressed={aktiv}
            title={aktiv && !gespeichert ? t.vorschlagTooltip : t.jaNeinHinweis}
            onClick={() => onWaehlen(v)}
            className={clsx(
              "px-2 py-1 text-xs font-medium transition-colors disabled:opacity-50",
              aktiv && gespeichert
                ? "bg-success text-white"
                : aktiv
                  ? "bg-accent-100 text-accent-700"
                  : "bg-surface text-text-2 hover:bg-surface-3"
            )}
          >
            {v}
          </button>
        );
      })}
    </div>
  );
}

/** Was ein Mensch an einer Entscheidung eingibt — Freitext ODER Kontaktfelder. */
export interface EntscheidungWahl {
  entscheidung?: string;
  kontakt?: Partial<Record<KontaktFeld, string>>;
}

/**
 * Alles, was eine Entscheidungs-Zeile braucht (B1: inline statt Stapel oben).
 *
 * Wird von `Review` durch `SektionsBlock` an `FrageKarte` gereicht. Fehlt es
 * (Reiter „Lücken"/„Anlagen"), rendert eine Entscheidungs-Zeile keinen Editor —
 * dort kommen ohnehin keine Entscheidungen vor.
 */
export interface EntscheidungDeko {
  kontaktVorlagen: Record<string, KontaktVorlage>;
  verzeichnis: KontaktVerzeichnisEintrag[];
  /** Frage-Id → Folgefragen dieses Bogens (aus den Entscheidungsketten). */
  kettenMap: Record<string, Frage[]>;
  /** Geteilte Kontaktwerte je Rolle — zwei Zeilen gleicher Rolle (2× ceo)
   *  teilen die Auswahl (B1). */
  rollenWerte: Record<string, Partial<Record<KontaktFeld, string>>>;
  setRollenWerte: (rolle: string, werte: Partial<Record<KontaktFeld, string>>) => void;
  onEntscheiden: (frage: Frage, wahl: EntscheidungWahl, folgefragenGelten: boolean) => void;
}

export interface FrageKarteProps {
  frage: Frage;
  /** Bibliotheks-Kennung → kanonische Frage. Seit der ⓘ-Entlastung (2026-08-21)
   *  in der Zeile nicht mehr angezeigt (Quellen-Hinweise entfallen); die
   *  Plumbing bleibt, falls eine Quellenansicht zurückkommen soll. */
  kanon: Record<string, string>;
  busy: boolean;
  onSetzen: (state: ReviewState, antwort?: FinaleAntwort | null) => void;
  onNeuSuchen: () => void;
  /** Entscheidungs-Kontext (nur im Reiter „Fragen" gesetzt). */
  deko?: EntscheidungDeko;
  /** B9: Anzeige-Nummer der wortgleichen Zwillingsfrage (falls es eine gibt). */
  duplikatVon?: number;
  /** B9: Antwort vom Zwilling übernehmen. */
  onDuplikatUebernehmen?: () => void;
}

export default function FrageKarte({
  frage,
  busy,
  onSetzen,
  onNeuSuchen,
  deko,
  duplikatVon,
  onDuplikatUebernehmen,
}: FrageKarteProps) {
  const t = useT().questionnaires;
  const s = frage.suggestion ?? {};
  const final = frage.final_answer ?? null;
  const [details, setDetails] = useState(false);
  const [bearbeitet, setBearbeitet] = useState(false);
  const [entwurf, setEntwurf] = useState("");
  // yes_no_text: Ja/Nein + Spezifikation getrennt bearbeiten.
  const istKombi = frage.answer_type === "yes_no_text";
  const [entwurfJaNein, setEntwurfJaNein] = useState("");
  const [entwurfSpez, setEntwurfSpez] = useState("");
  // B1: Entscheidungen klappen inline auf (kein Stapel mehr oben).
  const [entscheidungOffen, setEntscheidungOffen] = useState(false);

  const abgeschlossen = frage.review_state === "accepted" || frage.review_state === "edited";
  const vorschlag = String(s.answer ?? "");
  /** Was in der Zeile steht: die freigegebene Antwort, sonst der Vorschlag. */
  const wert = String(final?.answer ?? s.answer ?? "");
  const langText = String((abgeschlossen ? final?.answer_long : s.answer_long) ?? "");
  const istJaNein = frage.answer_type === "yes_no" || frage.answer_type === "attachment";
  /** `not_applicable` ist ausgeklammert, `decision` gehört dem Block oben. */
  const bedienbar = frage.kind !== "not_applicable" && frage.kind !== "decision";
  /**
   * „Das kann nur ein Mensch liefern" — Entscheidung UND Lücke.
   *
   * Aus Sicht des Prüfers sind das dieselbe Sorte Arbeit; dass die Entscheidung
   * bisher amber unterlegt war und die Lücke gar nicht, hat die Lücke im
   * Durchscrollen untergehen lassen. Eine Farbe für beide, und zwar die, die
   * schon „hier musst du hin" bedeutet.
   */
  const brauchtMensch =
    frage.kind === "decision" || (frage.kind === "gap" && frage.section_applicable);
  /**
   * Der sichere Vorschlag, den noch niemand angefasst hat.
   *
   * Er wird als Ergebnis gezeigt statt als Angebot mit Haken. Das ist eine
   * reine Darstellungsfrage: `review_state` bleibt `open`, bis die Attestierung
   * am Seitenende ihn übernimmt — die Regel „ein Mensch sieht jede Antwort,
   * bevor sie ins Dokument geht" (Konzept §2.2) hängt am Sehen, nicht am
   * Klicken. `skipped` ist bewusst ausgenommen: eine ausgelassene Frage darf
   * nicht fertig aussehen, und die Sammelübernahme fasst sie auch nicht an.
   */
  const sichererVorschlag =
    bedienbar &&
    frage.kind !== "gap" &&
    frage.review_state === "open" &&
    frage.confidence === "high" &&
    Boolean(vorschlag);
  /** Hat das ⓘ überhaupt noch etwas zu zeigen? Nach der Entlastung nur noch: der
   *  Grund einer gekappten Konfidenz, ein fehlender Faktenwert, oder die
   *  Nebenaktionen (Auslassen/Neu suchen) einer noch offenen Frage. Sonst
   *  entfällt der ⓘ-Knopf ganz — „nur noch die Erklärung, wenn es eine gibt". */
  const hatDetails =
    Boolean(s.konfidenz_grund) ||
    Boolean(s.fehlende_fakten?.length) ||
    (istJaNein && Boolean(langText)) ||
    (bedienbar && !abgeschlossen);

  // Entscheidungen mit EIGENEM Inline-Kontext (Kontakt, Umsatz, Beleg-Herausgabe)
  // brauchen den generischen „Commercial Team"-Satz nicht — der Editor sagt es
  // spezifisch, und bei einem Kontakt wäre „Commercial Team" ohnehin schief. Nur
  // die gewöhnliche Bibliotheks-Entscheidung (Freitext-Kontext) trägt ihn noch.
  const spezifischeEntscheidung =
    Boolean(s.umsatz) ||
    String(s.grund_code ?? "").startsWith("anlage_") ||
    Boolean(s.decision_context && s.decision_context.trim().startsWith("{"));

  /**
   * Der eine Satz, der auf der Zeile bleibt: warum diese Antwort so lautet
   * bzw. warum es keine gibt. Alles andere wandert hinter ⓘ — dieser Satz
   * nicht, weil er die Entscheidung des Prüfers trägt.
   */
  const kernSatz =
    frage.kind === "gap"
      ? s.norm_konflikt
        ? t.normKonflikt(
            s.norm_konflikt.quelle.join(", "),
            s.norm_konflikt.frage.join(", ")
          )
        : s.grund_code
          ? grundText(s, t)
          : `${t.keineBelegteAntwort} ${grundText(s, t)}`.trim()
      : frage.kind === "decision"
        ? spezifischeEntscheidung
          ? ""
          : t.entscheidetCommercial
        : s.grund_code
          ? grundText(s, t)
          : "";
  /** Ein Grund, der Handeln verlangt (abgelaufen, vertraulich, kein Beleg,
   *  Norm-Konflikt), wird gefärbt — ein Gating-„N/A" ist dagegen nur Info. */
  const kernWarnt =
    Boolean(s.norm_konflikt) || String(s.grund_code ?? "").startsWith("anlage_");

  const bearbeitenStarten = () => {
    if (istKombi) {
      setEntwurfJaNein(String(final?.ja_nein ?? s.ja_nein ?? ""));
      setEntwurfSpez(String(final?.spezifikation ?? s.spezifikation ?? ""));
    }
    setEntwurf(String(final?.answer ?? s.answer ?? ""));
    setBearbeitet(true);
  };

  const speichern = () => {
    if (istKombi) {
      const jn = entwurfJaNein.trim();
      const sp = entwurfSpez.trim();
      const antwort: FinaleAntwort = { answer: sp ? `${jn} — ${sp}` : jn, ja_nein: jn };
      if (sp) antwort.spezifikation = sp;
      onSetzen("edited", antwort);
      setBearbeitet(false);
      return;
    }
    const antwort: FinaleAntwort = { answer: entwurf.trim() };
    if (s.answer_long && entwurf.trim() === String(s.answer ?? "")) {
      antwort.answer_long = s.answer_long;
    }
    onSetzen("edited", antwort);
    setBearbeitet(false);
  };
  const kannSpeichern = istKombi ? Boolean(entwurfJaNein.trim()) : Boolean(entwurf.trim());

  /**
   * Ein Klick in der Zeile = genau ein PATCH.
   *
   * Trifft er den vorgeschlagenen Wert, ist das `accepted` — der Server setzt
   * die finale Antwort dann aus dem Vorschlag zusammen und behält dabei die
   * Langfassung. Trifft er einen anderen, ist es `edited` mit genau diesem
   * Wert; eine Langfassung zu einer Kurzantwort, die es nicht mehr gibt, wäre
   * im Antwortbogen ein Widerspruch.
   */
  const waehlen = (neu: string) => {
    if (busy) return;
    if (abgeschlossen && String(final?.answer ?? "") === neu) return;
    if (vorschlag && neu === vorschlag) {
      onSetzen("accepted");
      return;
    }
    onSetzen("edited", { answer: neu });
  };

  return (
    <div
      id={`frage-${frage.id}`}
      data-frage-art={frage.kind}
      data-review-state={frage.review_state}
      data-frage-zeile="1"
      className={clsx(
        "border-b border-border px-2 py-1.5 last:border-b-0 transition-colors",
        abgeschlossen
          ? "bg-success-soft/25"
          : brauchtMensch
            ? "bg-warning-soft/25"
            : "hover:bg-surface-3/40"
      )}
    >
      <div className="flex items-start gap-2">
        <span className="w-7 shrink-0 pt-1 text-[0.7rem] tabular-nums text-text-3">
          #{anzeigeNummer(frage.position)}
        </span>

        <div className="min-w-0 flex-1">
          {/* ── Frage LINKS · Antwort RECHTS ────────────────────────────────
              Ab 600 px trägt ein Raster die Trennung: der Fragetext bekommt
              eine eigene Spalte, die Antwortspalte darf wachsen und mehrzeilig
              werden — sie ist ein Block und kein Wettlauf um eine Zeile.
              Darunter wird gestapelt (Frage fett auf ihrer Zeile, Antwort
              darunter über die volle Breite). Bewusst eine arbiträre
              Media-Query und kein neuer Breakpoint in der tailwind.config:
              die Schwelle gilt für DIESE Zeile, nicht für die App. */}
          <div className="min-[600px]:grid min-[600px]:grid-cols-[minmax(150px,40%)_1fr] min-[600px]:items-start min-[600px]:gap-x-4">
            <p
              data-frage-text
              className="text-sm font-medium leading-snug text-text min-[600px]:font-normal"
            >
              {frage.question_text}
            </p>

            <div data-frage-antwort className="mt-1 min-w-0 min-[600px]:mt-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                {frage.kind === "decision" ? (
                  <>
                    {/* B1: kein Sprung mehr zum Stapel oben — die Entscheidung
                        klappt in dieser Zeile auf. Ist sie getroffen, steht die
                        Antwort hier und „Ändern" öffnet den Editor erneut. */}
                    {abgeschlossen && wert && (
                      <span
                        className="max-w-[10rem] truncate text-xs text-text sm:max-w-[16rem]"
                        title={wert}
                        data-entscheidung-antwort
                      >
                        {wert}
                      </span>
                    )}
                    {/* Beide Varianten setzen `text-text` — auf der amber
                        unterlegten Zeile war der Warnton als SCHRIFTfarbe zu
                        kontrastarm (Abnahme WH 2026-08-26). */}
                    <Button
                      size="sm"
                      variant={abgeschlossen ? "secondary" : "warning"}
                      disabled={busy || !deko}
                      aria-expanded={entscheidungOffen}
                      data-entscheiden-schalter
                      onClick={() => setEntscheidungOffen((o) => !o)}
                    >
                      {abgeschlossen ? t.aendern : t.entscheiden}
                    </Button>
                  </>
                ) : !bedienbar ? (
                  <StatusBadge status="neutral">{t.giltNicht}</StatusBadge>
                ) : sichererVorschlag ? (
                  <>
                    {/* Ergebnis, kein Angebot: der Wert steht als gewöhnlicher
                        Text da — nicht in einem Chip, der nach „bitte hier
                        entscheiden" aussieht. Voll sichtbar statt gekürzt: die
                        Antwortspalte darf umbrechen, und eine Antwort, die man
                        nur im Tooltip lesen kann, ist nicht geprüft. */}
                    <span
                      data-sicherer-vorschlag
                      title={t.sichererVorschlagTooltip}
                      className="min-w-0 break-words text-sm leading-snug text-text"
                    >
                      {wert}
                    </span>
                    {/* Leise, aber vorhanden. Ein umrandeter Knopf hier hiesse
                        wieder „tu was"; ein Link sagt „falls es falsch ist". */}
                    <button
                      type="button"
                      data-antwort-aendern
                      disabled={busy}
                      onClick={bearbeitenStarten}
                      title={t.antwortBearbeitenTooltip}
                      className="shrink-0 text-xs text-text-3 underline decoration-dotted underline-offset-2 hover:text-accent-600 disabled:opacity-50"
                    >
                      {t.aendern}
                    </button>
                  </>
                ) : istJaNein ? (
                  <>
                    {/* Ein eigener Text auf einer Ja/Nein-Frage: er steht neben
                        den Segmenten, sonst zeigte die Zeile eine leere Auswahl
                        und verschwiege die tatsächliche Antwort. */}
                    {wert && !JA_NEIN.includes(wert) && (
                      <span className="max-w-[8rem] truncate text-xs text-text-2" title={wert}>
                        {wert}
                      </span>
                    )}
                    <JaNeinLeiste
                      wert={wert}
                      gespeichert={abgeschlossen}
                      busy={busy}
                      onWaehlen={waehlen}
                    />
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      data-vorschlag-text
                      disabled={busy}
                      onClick={bearbeitenStarten}
                      title={wert || t.antwortBearbeitenTooltip}
                      className="max-w-[9rem] truncate rounded-md border border-border bg-surface px-2 py-1 text-left text-xs text-text hover:border-accent-300 disabled:opacity-50 sm:max-w-[22rem]"
                    >
                      {wert || <span className="text-text-3">{t.antwortEintragen}</span>}
                    </button>
                    {!abgeschlossen && vorschlag && (
                      <button
                        type="button"
                        data-antwort-uebernehmen
                        disabled={busy}
                        onClick={() => onSetzen("accepted")}
                        title={t.uebernehmen}
                        className="rounded-md border border-border p-1 text-success hover:bg-success-soft disabled:opacity-50"
                      >
                        <Check className="h-3.5 w-3.5" />
                        <span className="sr-only">{t.uebernehmen}</span>
                      </button>
                    )}
                  </>
                )}

                {/* Die Ampel nur da, wo sie etwas SAGT. „Sicher" ist der
                    Normalfall; ein grüner Punkt auf jeder zweiten Zeile war
                    Dekoration und zog den Blick genauso an wie das Amber
                    daneben, das wirklich Arbeit meint. Eine GEKAPPTE Konfidenz
                    bleibt sichtbar — sie ist ein Vorbehalt, kein Normalfall —,
                    ebenso mittel und unsicher. */}
                {frage.confidence &&
                  (frage.confidence !== "high" || Boolean(s.konfidenz_grund)) && (
                  <span
                    data-konfidenz={frage.confidence}
                    title={t.konfidenzPunktTooltip(konfidenzInfo(frage.confidence).label)}
                    className={clsx(
                      "relative inline-block h-2.5 w-2.5 shrink-0 rounded-full",
                      KONFIDENZ_PUNKT[frage.confidence]
                    )}
                  >
                    <span className="sr-only">
                      {t.konfidenzPunktTooltip(konfidenzInfo(frage.confidence).label)}
                    </span>
                  </span>
                )}

                {abgeschlossen && (
                  <span
                    data-abgeschlossen={frage.review_state === "edited" ? "korrigiert" : "uebernommen"}
                    title={frage.review_state === "edited" ? t.korrigiert : t.uebernommen}
                    className={clsx(
                      "inline-flex h-4 w-4 shrink-0 items-center justify-center",
                      frage.review_state === "edited" ? "text-warning" : "text-success"
                    )}
                  >
                    <Check className="h-4 w-4" />
                    <span className="sr-only">
                      {frage.review_state === "edited" ? t.korrigiert : t.uebernommen}
                    </span>
                  </span>
                )}
                {frage.review_state === "skipped" && frage.kind !== "gap" && (
                  <StatusBadge status="neutral" className="px-1.5 py-0 text-[0.65rem]">
                    {t.ausgelassen}
                  </StatusBadge>
                )}

                {hatDetails && (
                  <button
                    type="button"
                    data-details-schalter
                    aria-expanded={details}
                    onClick={() => setDetails((d) => !d)}
                    title={t.detailsTooltip}
                    className={clsx(
                      "shrink-0 rounded p-0.5 transition-colors",
                      details
                        ? "bg-accent-50 text-accent-700"
                        : "text-text-3 hover:bg-surface-3 hover:text-text"
                    )}
                  >
                    <Info className="h-4 w-4" />
                    <span className="sr-only">{t.details}</span>
                  </button>
                )}
              </div>

              {/* ── Was bei der Antwort bleiben muss ────────────────────────────
                  Steht in der ANTWORT-Spalte, nicht über die volle Breite:
                  fehlender Anker, Kern-Satz und Beleg-Marker erklären, was rechts
                  steht. Unter dem Fragetext eingerückt lasen sie sich wie eine
                  zweite Frage. */}
              {(kernSatz || !frage.anchor_kind || s.format_warnung || duplikatVon || s.anlage_marker_code) && (
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[0.7rem] leading-snug">
                  {/* B9: wortgleiches Duplikat im Formular — kennzeichnen, damit der
                      Verdacht „Bug" gar nicht erst entsteht, plus Antwort-Übernahme. */}
                  {duplikatVon !== undefined && (
                    <span className="inline-flex items-center gap-1 text-text-3" data-duplikat>
                      {t.duplikatHinweis(duplikatVon)}
                      {onDuplikatUebernehmen && !abgeschlossen && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={onDuplikatUebernehmen}
                          className="underline decoration-dotted underline-offset-2 hover:text-text disabled:opacity-50"
                        >
                          {t.duplikatUebernehmen(duplikatVon)}
                        </button>
                      )}
                    </span>
                  )}
                  {/* Der Anker steht NUR da, wo er fehlt: dann ist die Antwort
                      exportierbar, aber nicht einfügbar — das muss man sehen. */}
                  {!frage.anchor_kind && (
                    <span
                      className="inline-flex items-center gap-1 text-warning"
                      title={t.keinAnkerTooltip}
                      data-anker-fehlt
                    >
                      <AlertTriangle className="h-3 w-3 shrink-0" />
                      {t.ankerFehltKurz}
                    </span>
                  )}
                  {s.format_warnung && (
                    <span className="inline-flex items-center gap-1 text-warning">
                      <AlertTriangle className="h-3 w-3 shrink-0" />
                      {s.format_warnung}
                    </span>
                  )}
                  {frage.kind === "offen" && (
                    <span className="text-text-2">{t.nochNichtGesucht}</span>
                  )}
                  {kernSatz && (
                    <span
                      data-kern-satz
                      title={kernSatz}
                      className={clsx(
                        "min-w-0 max-w-full truncate",
                        kernWarnt ? "text-warning" : "text-text-2"
                      )}
                    >
                      {kernSatz}
                    </span>
                  )}
                  {/* „Do you have X? If yes, attach Y": neben dem Ja/Nein steht der
                      beizulegende Beleg — sonst geht er unter (WH 2026-08-21, #22).
                      „fehlt"/„Freigabe nötig" wird gefärbt (verlangt Handeln). */}
                  {s.anlage_marker_code && (
                    <span
                      data-anlage-marker={s.anlage_marker_code}
                      className={clsx(
                        "inline-flex min-w-0 items-center gap-1",
                        s.anlage_marker_code === "anlage_beilegen" ? "text-info" : "text-warning"
                      )}
                    >
                      <Paperclip className="h-3 w-3 shrink-0" />
                      <span className="truncate">
                        {t.anlageMarker(
                          s.anlage_marker_code,
                          s.anlage_titel ?? s.anlage?.bester?.title ?? ""
                        )}
                      </span>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── B1: Entscheidungs-Editor, inline aufgeklappt ───────────────── */}
          {frage.kind === "decision" && entscheidungOffen && deko && (
            <EntscheidungInline
              frage={frage}
              kontakt={deko.kontaktVorlagen[frage.id]}
              verzeichnis={deko.verzeichnis}
              folgefragen={deko.kettenMap[frage.id] ?? []}
              busy={busy}
              rollenWerte={deko.rollenWerte}
              setRollenWerte={deko.setRollenWerte}
              onAbbrechen={() => setEntscheidungOffen(false)}
              onEntscheiden={(wahl, gelten) => {
                deko.onEntscheiden(frage, wahl, gelten);
                setEntscheidungOffen(false);
              }}
            />
          )}

          {/* ── Inline-Editor (kein Modal, kein Sprung) ────────────────────── */}
          {bearbeitet && (
            <div className="mt-1.5 space-y-2" data-editor>
              {istKombi ? (
                // yes_no_text: Ja/Nein fürs Kästchen + Textfeld für die Angabe.
                <div className="space-y-2" data-kombi-editor>
                  <div className="flex flex-wrap items-center gap-2">
                    {JA_NEIN.map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setEntwurfJaNein(v)}
                        aria-pressed={entwurfJaNein === v}
                        className={clsx(
                          "px-3 py-1.5 rounded-md text-sm font-medium border transition-colors",
                          entwurfJaNein === v
                            ? "bg-accent-500 border-accent-500 text-white"
                            : "bg-surface border-border text-text-2 hover:border-accent-300"
                        )}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                  <Input
                    value={entwurfSpez}
                    onChange={(e) => setEntwurfSpez(e.target.value)}
                    placeholder={t.spezifikationPlatzhalter}
                    autoFocus
                  />
                </div>
              ) : (
                <AntwortFeld frage={frage} wert={entwurf} onChange={setEntwurf} autoFocus />
              )}
              {/* Zusatz-Information zur Antwort — bei EDITIERBAREN Fragen genau
                  dann, wenn man editiert (nicht dauerhaft unter jeder Zeile). Bei
                  Yes/No steht sie stattdessen hinter dem ⓘ (WH, 2026-08-21). */}
              {langText && (
                <p data-langfassung className="text-[0.72rem] leading-relaxed text-text-2">
                  {langText}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={speichern} disabled={busy || !kannSpeichern}>
                  {t.korrekturSpeichern}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setBearbeitet(false)}>
                  {t.abbrechen}
                </Button>
              </div>
            </div>
          )}

          {/* ── ⓘ: nur noch die Erklärung + Nebenaktionen ──────────────────
              Belege/Quellen-Hinweise, Herkunft und „Langfassung"-Block sind
              entfallen: das Review füllt NUR dieses Formular, nichts wandert in
              die Bibliothek — die Herkunfts-Erzählung trägt hier nicht mehr. Was
              bleibt, ist der Grund einer gekappten Konfidenz und ein fehlender
              Faktenwert; die eigentliche Info steht als Text unter der Zeile. */}
          {details && hatDetails && (
            <div
              data-frage-details
              className="mt-1.5 space-y-1.5 rounded-md border border-border bg-surface-3/50 px-2.5 py-2 text-[0.72rem] leading-relaxed text-text-2"
            >
              {/* Bei Yes/No steht die Zusatz-Information hier (man klickt Yes/No,
                  editiert keinen Text) — bei offenen Fragen erscheint sie
                  stattdessen im Editor. */}
              {istJaNein && langText && (
                <p data-langfassung className="text-text-2">
                  {langText}
                </p>
              )}
              {s.konfidenz_grund && (
                <p className="text-warning">
                  {s.konfidenz_grund_code === "anlage_zuordnung_mittel"
                    ? t.grundAnlageZuordnungMittel(
                        s.anlage?.bester?.title ?? "",
                        (s.anlage?.bester?.score ?? 0).toFixed(2)
                      )
                    : s.konfidenz_grund_code === "stammdaten_offener_todo"
                      ? t.grundStammdatenOffenerTodo(s.stammdaten?.key ?? "")
                      : t.konfidenzGrund(s.konfidenz_grund)}
                </p>
              )}
              {s.fehlende_fakten?.length ? (
                <p className="text-warning">
                  {t.fehlendeFakten(s.fehlende_fakten.join(", "))}
                </p>
              ) : null}

              {bedienbar && !abgeschlossen && (
                <div className="flex flex-wrap items-center gap-1 pt-0.5">
                  {frage.review_state !== "skipped" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<SkipForward className="h-3.5 w-3.5" />}
                      disabled={busy}
                      onClick={() => onSetzen("skipped")}
                    >
                      {t.auslassen}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Search className="h-3.5 w-3.5" />}
                    disabled={busy}
                    onClick={onNeuSuchen}
                    title={t.neuSuchenTooltip}
                  >
                    {t.neuSuchen}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Der Entscheidungs-Editor — inline in der Zeile (B1), nicht mehr als Stapel
 * oben und ohne Notiz-Feld. Vier Formen in EINEM Editor, damit der Reviewer
 * nicht rät, was gefragt ist:
 *
 * * **Kontakt** (B2): Dropdown aus dem Mitarbeiter-Verzeichnis + überschreibbare
 *   Felder; zwei Zeilen gleicher Rolle (2× ceo) teilen die Auswahl über
 *   `rollenWerte`.
 * * **Umsatz** (B3): die SF-Empfehlung (Betrag · Jahr · Band) plus „Band
 *   einsetzen"; die Weitergabe ist eine kaufmännische Freigabe — der Hinweis
 *   steht sichtbar dabei.
 * * **Beleg-Herausgabe** (B7): Ja/Nein mit erklärendem Kontext aus dem
 *   Katalog (nicht dem rohen Serversatz).
 * * **Freitext/Choice**: die gewöhnliche Entscheidung.
 */
function EntscheidungInline({
  frage,
  kontakt,
  verzeichnis,
  folgefragen,
  busy,
  rollenWerte,
  setRollenWerte,
  onEntscheiden,
  onAbbrechen,
}: {
  frage: Frage;
  kontakt?: KontaktVorlage;
  verzeichnis: KontaktVerzeichnisEintrag[];
  folgefragen: Frage[];
  busy: boolean;
  rollenWerte: Record<string, Partial<Record<KontaktFeld, string>>>;
  setRollenWerte: (rolle: string, werte: Partial<Record<KontaktFeld, string>>) => void;
  onEntscheiden: (wahl: EntscheidungWahl, folgefragenGelten: boolean) => void;
  onAbbrechen: () => void;
}) {
  const t = useT().questionnaires;
  const s: Vorschlag = frage.suggestion ?? {};
  const umsatz = s.umsatz;
  const istBeleg =
    s.grund_code === "anlage_fehlt" || s.grund_code === "anlage_vertraulich";

  const [wert, setWert] = useState("");
  const [manuell, setManuell] = useState<boolean | null>(null);
  // Vorbelegung: geteilte Rollenwerte gewinnen über die Faktenblatt-Vorgaben —
  // so übernimmt die zweite ceo-Zeile, was in der ersten getippt wurde.
  const [kontaktWerte, setKontaktWerte] = useState<Partial<Record<KontaktFeld, string>>>(
    () =>
      kontakt ? { ...(kontakt.vorgaben ?? {}), ...(rollenWerte[kontakt.rolle] ?? {}) } : {}
  );

  const folgefragenGelten = manuell ?? !/^(no|nein|n\/a)$/i.test(wert.trim());

  const setzeKontaktFeld = (feld: KontaktFeld, w: string) =>
    setKontaktWerte((v) => {
      const next = { ...v, [feld]: w };
      if (kontakt) setRollenWerte(kontakt.rolle, next);
      return next;
    });

  const waehlePerson = (id: string) => {
    const p = verzeichnis.find((e) => e.id === id);
    if (!p) return;
    const next: Partial<Record<KontaktFeld, string>> = {
      name: p.name,
      titel: p.titel ?? "",
      email: p.email,
      telefon: p.telefon ?? "",
    };
    setKontaktWerte(next);
    if (kontakt) setRollenWerte(kontakt.rolle, next);
  };

  const rolleLabel =
    (kontakt && (t.kontaktRollen as Record<string, string>)[kontakt.rolle]) ??
    kontakt?.rolle ??
    "";
  const kontaktTraegt = (kontakt?.ziel_felder ?? []).some((f) =>
    (kontaktWerte[f] ?? "").trim()
  );

  const absenden = () =>
    kontakt
      ? onEntscheiden({ kontakt: kontaktWerte }, folgefragenGelten)
      : onEntscheiden({ entscheidung: wert.trim() }, folgefragenGelten);

  const kontextText =
    s.decision_context && !s.decision_context.trim().startsWith("{")
      ? s.decision_context
      : null;

  return (
    <div
      className="mt-1.5 space-y-2.5 rounded-md border border-warning/40 bg-warning-soft/20 px-3 py-2.5"
      data-entscheidung-editor
    >
      {kontakt ? (
        <div className="space-y-2" data-kontakt-rolle={kontakt.rolle}>
          <p className="text-xs font-medium text-text">{t.kontaktFrage(rolleLabel)}</p>
          {/* B2: Auswahl aus dem Mitarbeiter-Verzeichnis. */}
          <div>
            <Select
              aria-label={t.verzeichnisWaehlen}
              value=""
              onChange={(e) => e.target.value && waehlePerson(e.target.value)}
            >
              <option value="">{t.verzeichnisWaehlen}</option>
              {verzeichnis.map((p) => (
                <option key={p.id} value={p.id}>
                  {t.verzeichnisOption(p.name, p.titel ?? p.department ?? "")}
                </option>
              ))}
            </Select>
            <p className="mt-1 text-xs text-text-3">{t.verzeichnisHinweis}</p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {kontakt.felder.map((feld) => (
              <label key={feld} className="block text-sm">
                <span className="text-xs text-text-2">{t.kontaktFeld[feld]}</span>
                <Input
                  className="mt-1"
                  data-kontakt-feld={feld}
                  value={kontaktWerte[feld] ?? ""}
                  onChange={(e) => setzeKontaktFeld(feld, e.target.value)}
                />
              </label>
            ))}
          </div>
          <p className="text-xs text-text-3">
            {t.kontaktZielFelder(
              kontakt.ziel_felder.map((f) => t.kontaktFeld[f]).join(", ")
            )}
          </p>
          {kontakt.fehlende_fakten.length > 0 && (
            <p className="text-xs text-text-3">
              {t.kontaktFehlendeFakten(kontakt.fehlende_fakten.join(", "))}
            </p>
          )}
        </div>
      ) : umsatz ? (
        <div className="space-y-2" data-umsatz-editor>
          <div className="rounded-md border border-border bg-surface px-2.5 py-2 text-xs">
            <p className="font-medium text-text">{t.umsatzTitel}</p>
            {umsatz.empfehlung && (
              <p className="mt-0.5 text-text">{t.umsatzEmpfehlung(umsatz.empfehlung)}</p>
            )}
            <p className="mt-0.5 leading-relaxed text-text-2">
              {umsatz.quelle === "scenario_master" && umsatz.summe_eur !== undefined
                ? t.umsatzBasisScenario(
                    umsatz.jahr ?? 0,
                    umsatz.summe_eur.toLocaleString("de-DE"),
                    (umsatz.summe_usd ?? 0).toLocaleString("de-DE")
                  )
                : t.umsatzBasisFakt(umsatz.empfehlung ?? "—")}
            </p>
            <p className="mt-1 text-warning">{t.umsatzFreigabeHinweis}</p>
          </div>
          {umsatz.empfehlung && (
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => setWert(umsatz.empfehlung as string)}
            >
              {t.umsatzUebernehmen}
            </Button>
          )}
          <AntwortFeld frage={frage} wert={wert} onChange={setWert} autoFocus />
        </div>
      ) : (
        <div className="space-y-2">
          {istBeleg ? (
            <div className="rounded-md border border-border bg-surface px-2.5 py-2 text-xs leading-relaxed text-text-2">
              <p className="font-medium text-text">{t.belegHerausgabeFrage}</p>
              <p className="mt-0.5">
                {s.grund_code === "anlage_vertraulich"
                  ? t.belegHerausgabeVertraulich(s.anlage?.bester?.title ?? "")
                  : t.belegHerausgabeFehlt}
              </p>
            </div>
          ) : (
            kontextText && (
              <div className="rounded-md border border-border bg-surface px-2.5 py-2 text-xs">
                <span className="text-[0.65rem] uppercase tracking-wide text-text-3">
                  {t.entscheidungsKontext}
                </span>
                <p className="mt-0.5 leading-relaxed text-text-2">{kontextText}</p>
              </div>
            )
          )}
          <AntwortFeld frage={frage} wert={wert} onChange={setWert} autoFocus />
        </div>
      )}

      {folgefragen.length > 0 && (
        <div className="text-sm">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-1"
              checked={folgefragenGelten}
              onChange={(e) => setManuell(e.target.checked)}
            />
            <span>
              {t.folgefragenBleiben}
              <span className="mt-0.5 block text-xs text-text-2">
                {folgefragenGelten ? t.folgefragenBleibenJa : t.folgefragenBleibenNein}
              </span>
            </span>
          </label>
          <ul className="mt-1 space-y-0.5 text-xs text-text-2">
            {folgefragen.map((ff) => (
              <li key={ff.id}>
                #{anzeigeNummer(ff.position)} {ff.question_text}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={busy || (kontakt ? !kontaktTraegt : !wert.trim())}
          onClick={absenden}
        >
          {kontakt ? t.kontaktUebernehmen : t.entscheidungSpeichern}
        </Button>
        <Button size="sm" variant="ghost" onClick={onAbbrechen} disabled={busy}>
          {t.abbrechen}
        </Button>
      </div>
    </div>
  );
}
